import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ScreenShare, Video } from 'lucide-react';
import { useI18n } from '../../lib/i18n';
import { canShareHere, useShare } from './ShareProvider';
import { useRec } from '../rec/RecProvider';

/**
 * "Share screen" en la barra superior, entre el reloj y el perfil. Solo existe si el usuario PUEDE
 * COMPARTIR (office+ habilitado; el inspector solo recibe), el navegador puede capturar (los
 * teléfonos no tienen getDisplayMedia) y la pantalla es de escritorio (≤900 px no se pinta).
 * ≤1280 px queda solo el icono, como los links de la barra.
 *
 * Botón DIVIDIDO (spec §4.9): el clic abre un menú de dos entradas — "Share live" (el selector de
 * compañero) y "Record a video" (spec §4.12: el modal de Record del `RecProvider`, si `/me` trae
 * `record_enabled`; si no, deshabilitado con "Coming soon"). `onRecord` sigue existiendo para quien
 * quiera otro destino; sin él se usa el provider.
 */
export function ShareButton({ onRecord }: { onRecord?: () => void } = {}) {
  const { t } = useI18n();
  const s = useShare();
  const rec = useRec();
  const record: (() => void) | undefined = onRecord ?? (rec.canRecord ? rec.openSetup : undefined);
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches);
  const [menu, setMenu] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  // El menú se cierra con un clic afuera o con Esc.
  useEffect(() => {
    if (!menu) return undefined;
    const onDown = (e: PointerEvent) => { if (!wrapRef.current?.contains(e.target as Node)) setMenu(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(false); };
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('pointerdown', onDown, true); window.removeEventListener('keydown', onKey); };
  }, [menu]);

  // Con Record prendido y el share apagado el botón existe igual: solo "Share live" queda deshabilitado.
  if ((!s.canShare && !record) || narrow || !canShareHere()) return null;

  const sharer = s.broadcast && s.role === 'sharer';
  const viewer = s.active && s.role === 'viewer';
  let label = t('share.button');
  let cls = 'btn ghost sm ss-btn';
  let onClick: (() => void) | undefined = () => setMenu((m) => !m);
  let disabled = false;
  let withMenu = true;
  if (sharer && s.mine) {
    // Compartiendo desde acá: la píldora tiene Stop/Mute/Add; el botón solo informa (no simula un clic).
    const active = s.broadcast!.participants.filter((p) => p.status === 'accepted' || p.status === 'live');
    label = active.length > 1 ? t('share.sharingWithN', { n: active.length })
      : active.length === 1 ? t('share.sharingWith', { name: active[0]!.viewer.name })
        : t('share.button');
    cls += ' on';
    disabled = true;
    onClick = undefined;
    withMenu = false;
  } else if (sharer && !s.mine) {
    label = t('share.otherTab');
    cls += ' on';
    disabled = true;
    onClick = undefined;
    withMenu = false;
  } else if (viewer) {
    // Mirando: el clic restaura el visor minimizado (o trae el video si se fue a otra ventana).
    label = t('share.viewing', { name: s.active!.sharer.name });
    cls += ' on';
    onClick = s.elsewhere && !s.mine ? s.bringBack : () => s.setMinimized(false);
    withMenu = false;
  }

  return (
    <div className="ss-btn-wrap" ref={wrapRef}>
      <button type="button" className={cls} onClick={onClick} disabled={disabled} title={label}
        aria-haspopup={withMenu ? 'menu' : undefined} aria-expanded={withMenu ? menu : undefined}>
        <ScreenShare size={16} strokeWidth={1.75} />
        <span className="ss-btn-label">{label}</span>
        {withMenu && <ChevronDown size={13} strokeWidth={2} aria-hidden />}
      </button>
      {withMenu && menu && (
        <div className="ss-menu" role="menu">
          <button type="button" role="menuitem" className="ss-menu-item" disabled={!s.canShare}
            title={s.canShare ? undefined : t('share.notEnabledMe')}
            onClick={() => { setMenu(false); s.openPicker(); }}>
            <ScreenShare size={16} strokeWidth={1.75} /> {t('share.modeLive')}
          </button>
          <button type="button" role="menuitem" className="ss-menu-item" disabled={!record || rec.isRecording}
            title={record ? undefined : t('share.comingSoon')}
            onClick={() => { setMenu(false); record?.(); }}>
            <Video size={16} strokeWidth={1.75} /> {t('share.modeRecord')}
            {!record && <small>{t('share.comingSoon')}</small>}
          </button>
        </div>
      )}
    </div>
  );
}
